import { createEntityAdapter, createSelector, createSlice, current, Draft, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { uploadPrivateAsset } from '@ff-os/pdp-shared-redux/apis/private-assets/uploadPrivateAsset';
import { Events, pushEvent } from '@ff-os/shared-react/utils/analytics';
import { isInRange } from '@ff-os/shared-utils/isInRange';
import { Area } from '@ff-os/shared-utils/types/Area';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import cuid from 'cuid';
import { selectAccessoryMetadataBySizeId, selectSizeById } from '../selectors/sizeSelectors';
import { selectVariantById } from '../selectors/variantSelectors';
import { PhotoPrintsPdpState } from '../store';
import { handleFulfilled, handlePending, handleRejected } from './apis/privateAssetHandling';
import { selectGroupById } from './groupsSlice';

const adapter = createEntityAdapter<PhotoPrintsJson.Photo>();
const adapterSelectors = adapter.getSelectors<PhotoPrintsPdpState>(s => s.project.photos);

export const addOne = (
    state: EntityState<PhotoPrintsJson.Photo>,
    photo: PhotoPrintsJson.Photo
) => {
    return adapter.addOne(state, photo);
};

export const { actions, reducer } = createSlice({
    name: 'photos',
    initialState: adapter.getInitialState(),
    reducers: {
        add: (state, action: PayloadAction<PhotoPrintsJson.Photo>) => {
            return addOne(state, action.payload);
        },

        addMany: adapter.addMany,

        setAll: adapter.setAll,
        updateOne: adapter.updateOne,
        remove: adapter.removeOne,
        removeMany: adapter.removeMany,

        setQuantity: (state, action: PayloadAction<{ photoId: string; quantity: number }>) => {
            const { photoId, quantity } = action.payload;
            state.entities[photoId]!.quantity = quantity;
        },

        move: (
            state,
            action: PayloadAction<{ photoId: string; groupId: string }>
        ) => {
            const { photoId, groupId } = action.payload;
            const photo = state.entities[photoId]! as Draft<PhotoPrintsJson.ProcessedPhoto>;
            photo.groupId = groupId;

            // Magic border properties are now determined from group, not photo
            // Clear photo-specific magic border data when moving
            photo.magicBorderRngSeed = `${photo!.id}-${Date.now()}`;
            photo.reproString = undefined;

            const facesInCropBox = current(photo.detections).filter(d => isInRange(d, current(photo.crop)));
            photo.autoFaceCenter = facesInCropBox.length > 0;
        },

        copy: (state, action: PayloadAction<{ photoId: string; groupId: string }>) => {

            const { photoId, groupId } = action.payload;
            const photo = state.entities[photoId]! as Draft<PhotoPrintsJson.ProcessedPhoto>;

            const facesInCropBox = current(photo.detections).filter(d => isInRange(d, current(photo.crop)));

            const newPhotoId = cuid();

            const newPhoto = {
                ...current(photo),
                groupId,
                id: newPhotoId,
                autoFaceCenter: facesInCropBox.length > 0,
                magicBorderRngSeed: `${photo!.id}-${Date.now()}`,
                reproString: undefined
            };

            adapter.addOne(state, newPhoto);
        },

        updateCrop: (state, action: PayloadAction<{ photoId: string; crop: Area }>) => {
            const { photoId, crop } = action.payload;
            const photo = state.entities[photoId]! as Draft<PhotoPrintsJson.ProcessedPhoto>;
            photo.crop = crop;
            photo.autoFaceCenter = false;
            photo.reproString = undefined;
        },

        updateFilter: (state, action: PayloadAction<{ photoId: string; filter: string | null }>) => {
            const { photoId, filter } = action.payload;
            const photo = state.entities[photoId]! as Draft<PhotoPrintsJson.ProcessedPhoto>;
            photo.filter = filter;
            photo.reproString = undefined;
        },

        removeBorder: (
            state,
            action: PayloadAction<{
                groupId: string;
            }>
        ) => {
            const { groupId } = action.payload;

            Object.values(state.entities)
                .filter(photo => photo?.groupId === groupId && photo.processed)
                .forEach(photo => {

                    const processedPhoto = photo as Draft<PhotoPrintsJson.ProcessedPhoto>;

                    processedPhoto.magicBorderRngSeed = undefined;
                    processedPhoto.reproString = undefined;
                });

        },

        applyMagicBorder: (
            state,
            action: PayloadAction<{
                groupId: string;
                magicBorderId: string;
            }>
        ) => {

            const { groupId, magicBorderId } = action.payload;

            const photosInGroup = Object.values(state.entities)
                .filter(photo => photo?.groupId === groupId && photo.processed) as PhotoPrintsJson.ProcessedPhoto[];

            pushEvent(Events.APPLY_MAGIC_BORDER, {
                groupId: groupId,
                magicBorderId: magicBorderId,
                imagesCount: photosInGroup.length
            });

            photosInGroup.forEach(photo => {

                const processedPhoto = photo as Draft<PhotoPrintsJson.ProcessedPhoto>;

                // Only generate RNG seed for magic border configs
                if (!processedPhoto.magicBorderRngSeed || processedPhoto.magicBorderRngSeed === "") {
                    processedPhoto.magicBorderRngSeed = `${photo!.id}-${Date.now()}`;
                }
                // Clear any existing reproduction string when applying new border
                processedPhoto.reproString = undefined;
            });
        },

        applyPattern: (
            state,
            action: PayloadAction<{
                groupId: string;
                patternId: string;
            }>
        ) => {

            const { groupId, patternId } = action.payload;

            const photosInGroup = Object.values(state.entities)
                .filter(photo => photo?.groupId === groupId && photo.processed) as PhotoPrintsJson.ProcessedPhoto[];

            pushEvent(Events.APPLY_PATTERN, {
                groupId: groupId,
                patternId: patternId,
                imagesCount: photosInGroup.length
            });

            // Clear reproduction strings when switching to pattern
            photosInGroup.forEach(photo => {
                const processedPhoto = photo as Draft<PhotoPrintsJson.ProcessedPhoto>;
                processedPhoto.reproString = undefined;
                processedPhoto.magicBorderRngSeed = undefined;
            });
        },

        removeMagicBorder: (
            state,
            action: PayloadAction<{
                groupId: string;
            }>
        ) => {

            const { groupId } = action.payload;

            pushEvent(Events.REMOVE_MAGIC_BORDER, {
                groupId: groupId,
                imagesCount: Object.values(state.entities)
                    .filter(photo => photo?.groupId === groupId && photo.processed).length
            });

            Object.values(state.entities)
                .filter(photo => photo?.groupId === groupId && photo.processed)
                .forEach(photo => {
                    const processedPhoto = photo as Draft<PhotoPrintsJson.ProcessedPhoto>;
                    processedPhoto.magicBorderRngSeed = undefined;
                    processedPhoto.reproString = undefined;
                });
        },

        clearReproStrings: (
            state,
            action: PayloadAction<{
                groupId: string;
            }>
        ) => {

            const { groupId } = action.payload;

            Object.values(state.entities)
                .filter(photo => photo?.groupId === groupId)
                .forEach(photo => {
                    const processedPhoto = photo as Draft<PhotoPrintsJson.ProcessedPhoto>;
                    processedPhoto.reproString = undefined;
                });
        },

        removePattern: (
            state,
            action: PayloadAction<{
                groupId: string;
            }>
        ) => {

            const { groupId } = action.payload;

            pushEvent(Events.REMOVE_PATTERN, {
                groupId: groupId,
                imagesCount: Object.values(state.entities)
                    .filter(photo => photo?.groupId === groupId && photo.processed).length
            });
        },

        addAccessoryToPhoto: (
            state,
            action: PayloadAction<{
                photoId: string;
                accessoryId: string;
                sizeId: string;
            }>
        ) => {

            const { photoId, accessoryId, sizeId } = action.payload;
            const photo = state.entities[photoId] as Draft<PhotoPrintsJson.ProcessedPhoto>;

            if (photo && photo.processed) {
                const previousAccessoryId = photo.accessoryId;

                photo.accessoryId = accessoryId;
                photo.accessorySizeId = sizeId;

                if (previousAccessoryId && previousAccessoryId !== accessoryId) {
                    pushEvent(Events.CHANGE_ACCESSORY, {
                        photoId,
                        previousAccessoryId,
                        newAccessoryId: accessoryId,
                        sizeId
                    });
                } else if (!previousAccessoryId) {
                    pushEvent(Events.ADD_ACCESSORY, {
                        photoId,
                        accessoryId,
                        sizeId
                    });
                }
            }
        },

        removeAccessoryFromPhoto: (
            state,
            action: PayloadAction<{
                photoId: string;
            }>
        ) => {

            const { photoId } = action.payload;
            const photo = state.entities[photoId] as Draft<PhotoPrintsJson.ProcessedPhoto>;

            if (photo && photo.processed && photo.accessoryId) {
                const previousAccessoryId = photo.accessoryId;

                photo.accessoryId = undefined;
                photo.accessorySizeId = undefined;

                pushEvent(Events.REMOVE_ACCESSORY, {
                    photoId,
                    accessoryId: previousAccessoryId
                });
            }
        },

        saveReproString: (
            state,
            action: PayloadAction<{
                photoId: string;
                reproString: string;
            }>
        ) => {
            const { photoId, reproString } = action.payload;
            const photo = state.entities[photoId] as Draft<PhotoPrintsJson.ProcessedPhoto> | undefined;
            if (photo) {
                photo.reproString = reproString;
            }
        },

        setCapturedOn: (
            state,
            action: PayloadAction<{ photoId: string; capturedOn: string }>
        ) => {
            const { photoId, capturedOn } = action.payload;
            const photo = state.entities[photoId] as Draft<PhotoPrintsJson.ProcessedPhoto> | undefined;
            if (photo) {
                if (capturedOn) {
                    photo.capturedOn = capturedOn;
                } else {
                    delete photo.capturedOn;
                }
            }
        }
    },

    extraReducers: builder => {
        builder.addCase(uploadPrivateAsset.pending, handlePending);
        builder.addCase(uploadPrivateAsset.rejected, handleRejected);
        builder.addCase(uploadPrivateAsset.fulfilled, handleFulfilled);
    }
});

export const selectHasErrorByGroupId = createSelector([
    (state: PhotoPrintsPdpState) => selectAllPhotos(state),
    (state: PhotoPrintsPdpState, groupId: string) => groupId
], (photos, groupId) => {
    return photos
        .filter(p => p.groupId === groupId)
        .some(p => p.status === 'error');
});

export const selectPhotoIdsByGroupId = createSelector([
    (state: PhotoPrintsPdpState) => selectAllPhotos(state),
    (state: PhotoPrintsPdpState, groupId: string) => groupId
], (photos, groupId) => {
    return photos
        .filter(p => p.groupId === groupId)
        .map(p => p.id);
});

export const selectPhotosByGroupId = createSelector([
    (state: PhotoPrintsPdpState) => adapterSelectors.selectAll(state),
    (state: PhotoPrintsPdpState, groupId: string) => groupId
], (photos, groupId) => {
    return Object.values(photos).filter(p => p!.groupId === groupId);
});

export const selectPhotoById = adapterSelectors.selectById;
export const selectAllPhotos = adapterSelectors.selectAll;

const isMissingDate = (photo: PhotoPrintsJson.Photo | undefined): photo is PhotoPrintsJson.ProcessedPhoto =>
    !!photo && photo.processed && !photo.capturedOn;

export const selectDatablePhotoIdsByGroupIdSortedByName = createSelector([
    selectAllPhotos,
    (_state: PhotoPrintsPdpState, groupId: string) => groupId
], (photos, groupId) => photos
    .filter(photo => photo.groupId === groupId && photo.processed)
    .sort((a, b) => (a.fileName ?? '').localeCompare(b.fileName ?? ''))
    .map(photo => photo.id));

export const selectMissingDateCountByGroupId = createSelector([
    selectAllPhotos,
    (_state: PhotoPrintsPdpState, groupId: string) => groupId
], (photos, groupId) => photos.filter(p => p.groupId === groupId && isMissingDate(p)).length);

export const selectMissingDateCountByGroup = createSelector([
    selectAllPhotos
], (photos) => photos.filter(isMissingDate).reduce((counts, photo) => {
    counts[photo.groupId] = (counts[photo.groupId] ?? 0) + 1;
    return counts;
}, {} as Record<string, number>));

export const selectAllProcessedPhotos = createSelector([
    selectAllPhotos
], (photos) => photos.filter(p => p.processed) as PhotoPrintsJson.ProcessedPhoto[]);

export const selectHasAccessoryApplied = createSelector([
    (state: PhotoPrintsPdpState, groupId: string) => selectGroupById(state, groupId),
    (state: PhotoPrintsPdpState, groupId: string) => state
], (group, state) => {
    if (!group) return false;
    const variant = selectVariantById(state, group.variantId);
    if (!variant) return false;
    const size = selectSizeById(state, variant.sizeId);
    return size?.isAccessory === true;
});

export const selectActiveAccessoryId = createSelector([
    (state: PhotoPrintsPdpState, groupId: string) => selectGroupById(state, groupId),
    (state: PhotoPrintsPdpState, groupId: string) => state
], (group, state) => {
    if (!group) return null;
    const variant = selectVariantById(state, group.variantId);
    if (!variant) return null;
    const size = selectSizeById(state, variant.sizeId);
    return size?.isAccessory === true ? size.id : null;
});

export const selectActiveAccessorySizeId = createSelector([
    (state: PhotoPrintsPdpState) => selectAllPhotos(state),
    (state: PhotoPrintsPdpState, groupId: string) => groupId
], (photos, groupId) => {

    const photoWithAccessory = photos.find(photo =>
        photo.groupId === groupId &&
        photo.processed &&
        (photo as PhotoPrintsJson.ProcessedPhoto).accessoryId
    ) as PhotoPrintsJson.ProcessedPhoto | undefined;

    return photoWithAccessory?.accessorySizeId || null;
});

export const hasValidAccessoryQuantityByGroupId = createSelector([
    (state: PhotoPrintsPdpState) => state,
    (state: PhotoPrintsPdpState, groupId: string) => groupId,
    (state: PhotoPrintsPdpState, groupId: string) => selectHasAccessoryApplied(state, groupId),
    (state: PhotoPrintsPdpState, groupId: string) => selectActiveAccessoryId(state, groupId)
], (state, groupId, hasAccessoriesApplied, accessoryId) => {

    if (!hasAccessoriesApplied || !accessoryId) {
        return true;
    }

    const photos = selectAllPhotos(state);
    const quantity = photos
        .filter(photo => photo.groupId === groupId && photo.processed)
        .reduce((sum, photo) => sum + (photo.quantity ?? 1), 0);

    const accessoryMetadata = selectAccessoryMetadataBySizeId(state, accessoryId);
    const minimumAccessoryQuantity = accessoryMetadata?.minimumQuantity || 1;

    return !minimumAccessoryQuantity || quantity % minimumAccessoryQuantity === 0;
});

export const getAccessoryValidationByGroupId = createSelector([
    (state: PhotoPrintsPdpState) => state,
    (state: PhotoPrintsPdpState, groupId: string) => groupId,
    (state: PhotoPrintsPdpState, groupId: string) => selectHasAccessoryApplied(state, groupId),
    (state: PhotoPrintsPdpState, groupId: string) => selectActiveAccessoryId(state, groupId)
], (state, groupId, hasAccessoriesApplied, accessoryId) => {

    if (!hasAccessoriesApplied || !accessoryId) {
        return {
            minimumRequired: 0,
            shortage: 0,
            isValid: true
        };
    }

    const photos = selectAllPhotos(state);
    const quantity = photos
        .filter(photo => photo.groupId === groupId && photo.processed)
        .reduce((sum, photo) => sum + (photo.quantity ?? 1), 0);

    const accessoryMetadata = selectAccessoryMetadataBySizeId(state, accessoryId);
    const minimumRequired = accessoryMetadata?.minimumQuantity || 1;

    if (!minimumRequired) {
        return {
            minimumRequired: 0,
            shortage: 0,
            isValid: true
        };
    }

    const remainder = quantity % minimumRequired;
    const shortage = remainder === 0 ? 0 : minimumRequired - remainder;

    return {
        minimumRequired,
        shortage,
        isValid: shortage === 0
    };
});

export const hasValidAccessoryQuantities = createSelector([
    (state: PhotoPrintsPdpState) => selectAllPhotos(state),
    (state: PhotoPrintsPdpState) => state
], (photos, state) => {

    const groupIds = [...new Set(photos.map(p => p.groupId))];

    return groupIds.every(groupId => hasValidAccessoryQuantityByGroupId(state, groupId));
});

export const selectFirstGroupWithAccessoryError = createSelector([
    (state: PhotoPrintsPdpState) => selectAllPhotos(state),
    (state: PhotoPrintsPdpState) => state
], (photos, state) => {

    const groupIds = [...new Set(photos.map(p => p.groupId))];

    return groupIds.find(groupId => !hasValidAccessoryQuantityByGroupId(state, groupId)) || null;
});